import { useState, useEffect } from 'react';
import { getServices, getServicesByCategorySlug } from '../services/api';
import type { BackendService } from '../types/api';

interface UseServicesOptions {
  bookableOnly?: boolean;
}

export const useServices = (
  categoryId?: number,
  options: UseServicesOptions = {},
) => {
  const [services, setServices] = useState<BackendService[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { bookableOnly = false } = options;

  useEffect(() => {
    const fetchServices = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getServices(categoryId, bookableOnly);
        setServices(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch services');
        console.error('Error fetching services:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, [categoryId, bookableOnly]);

  return { services, loading, error };
};

export const useServicesByCategory = (
  categorySlug: string,
  options: UseServicesOptions = {},
) => {
  const [services, setServices] = useState<BackendService[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { bookableOnly = false } = options;

  useEffect(() => {
    const fetchServices = async () => {
      try {
        setLoading(true);
        setError(null);

        if (!categorySlug) {
          setServices([]);
          return;
        }

        const data = await getServicesByCategorySlug(categorySlug, bookableOnly);
        setServices(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch services');
        console.error('Error fetching services:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, [categorySlug, bookableOnly]);

  return { services, loading, error };
};
